import { motion } from "motion/react";
import { AlertTriangle, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AccountantInboxTenantRef } from "@fiscalo/shared-types";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export const BANDEJA_TENANT_FILTER_ALL = "all";

export function tenantDisplayName(tenant: AccountantInboxTenantRef): string {
  return tenant.legalName ?? tenant.name ?? tenant.rut ?? "—";
}

interface BandejaFilterBarProps {
  /** Tenants currently in the queue. Built per-page from the loaded
   *  items, so the dropdown only ever shows clients with pending work
   *  in this section. */
  tenantOptions: AccountantInboxTenantRef[];
  tenantFilter: string;
  onTenantFilterChange: (value: string) => void;
  overdueOnly: boolean;
  onToggleOverdue: () => void;
  /** Free-text search. The caller owns the predicate (different
   *  fields per queue), the bar just renders the input. Empty string
   *  means no search active. */
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchPlaceholder: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Spanish gender-agreement matters here — "Vencidas" (f) for ACKs,
   *  "Vencidos" (m) for boletos/obligaciones. Caller decides. */
  overdueLabel: string;
  reduceMotion: boolean;
}

/**
 * Filter row shared across all three bandeja list pages. Renders a
 * tenant single-select + an overdue toggle pill; both filters live in
 * URL search params managed by the parent page so views stay
 * bookmarkable. Designed to be skipped when there are no pending items
 * at all (the empty-state card replaces the whole table).
 */
export function BandejaFilterBar({
  tenantOptions,
  tenantFilter,
  onTenantFilterChange,
  overdueOnly,
  onToggleOverdue,
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder,
  hasActiveFilters,
  onClearFilters,
  overdueLabel,
  reduceMotion,
}: BandejaFilterBarProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-wrap items-center gap-3 rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] bg-white px-3 py-2 shadow-[0_1px_0_rgba(22,21,20,0.03)]"
    >
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {t("bandeja.filters.tenant", "Cliente")}
        </span>
        <Select value={tenantFilter} onValueChange={onTenantFilterChange}>
          <SelectTrigger className="h-8 w-[200px] text-sm">
            <SelectValue
              placeholder={t("bandeja.filters.allTenants", "Todos")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BANDEJA_TENANT_FILTER_ALL}>
              {t("bandeja.filters.allTenants", "Todos")}
            </SelectItem>
            {tenantOptions.map((tn) => (
              <SelectItem key={tn.id} value={tn.id}>
                {tenantDisplayName(tn)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <button
        type="button"
        onClick={onToggleOverdue}
        aria-pressed={overdueOnly}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)]",
          overdueOnly
            ? "border-[var(--fx-warn)]/40 bg-[var(--fx-warn)]/10 text-[var(--fx-warn-text)]"
            : "border-[var(--fx-ink-200)] bg-white text-muted-foreground hover:border-[var(--fx-ink-300)] hover:text-foreground",
        )}
      >
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        {overdueLabel}
      </button>

      <div className="relative w-[220px]">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-8 pl-7 pr-7 text-sm"
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => onSearchQueryChange("")}
            aria-label="Limpiar búsqueda"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:bg-[var(--fx-s-raised-hover)] hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
      </div>

      {hasActiveFilters ? (
        <button
          type="button"
          onClick={onClearFilters}
          className="ml-auto text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("bandeja.filters.clear", "Limpiar filtros")}
        </button>
      ) : null}
    </motion.div>
  );
}
